const FEATURE_SELECTOR = 'main islander-collection-feature[data-layout-mode]';
const THEME_EDITOR_EVENTS = [
  'shopify:section:load',
  'shopify:section:unload',
  'shopify:section:reorder',
];

export function resolveCollectionLayouts(modes) {
  return modes.map((mode, index) => {
    if (mode === 'image_left') return 'image-left';
    if (mode === 'image_right') return 'image-right';
    return index % 2 === 0 ? 'image-left' : 'image-right';
  });
}

export function getCollectionCarouselState(pairCount, visiblePairs, requestedIndex) {
  const resolvedPairCount = Math.max(0, Math.floor(Number(pairCount) || 0));
  const resolvedVisiblePairs = Math.max(
    1,
    Math.min(resolvedPairCount || 1, Math.floor(Number(visiblePairs) || 1)),
  );
  const maxIndex = Math.max(0, resolvedPairCount - resolvedVisiblePairs);
  const index = Math.min(maxIndex, Math.max(0, Math.floor(Number(requestedIndex) || 0)));

  return {
    index,
    maxIndex,
    canGoPrevious: index > 0,
    canGoNext: index < maxIndex,
    firstProduct: resolvedPairCount === 0 ? 0 : index * 2 + 1,
    lastProduct:
      resolvedPairCount === 0
        ? 0
        : Math.min(resolvedPairCount * 2, (index + resolvedVisiblePairs) * 2),
  };
}

export function getCollectionCarouselLoopIndex(pairCount, requestedIndex) {
  const count = Math.max(0, Math.floor(Number(pairCount) || 0));
  if (!count) return 0;
  const requested = Math.floor(Number(requestedIndex) || 0);
  return ((requested % count) + count) % count;
}

export function getCollectionCarouselAutoScrollIndex(pairCount, visiblePairs, requestedIndex) {
  void visiblePairs;
  return getCollectionCarouselLoopIndex(pairCount, Number(requestedIndex) + 1);
}

const COLLECTION_CAROUSEL_AUTO_SCROLL_INTERVAL = 5000;
const COLLECTION_CAROUSEL_RESUME_DELAY = 4500;
let layoutFrame;

function applyCollectionLayouts() {
  const sections = Array.from(document.querySelectorAll(FEATURE_SELECTOR));
  const modes = sections.map((section) => section.dataset.layoutMode || 'auto');
  const resolvedLayouts = resolveCollectionLayouts(modes);
  sections.forEach((section, index) => {
    section.dataset.resolvedLayout = resolvedLayouts[index];
  });
}

function scheduleCollectionLayoutUpdate() {
  if (layoutFrame) window.cancelAnimationFrame(layoutFrame);
  layoutFrame = window.requestAnimationFrame(() => {
    layoutFrame = undefined;
    applyCollectionLayouts();
  });
}

function registerCollectionLandingElements() {
  class IslanderCollectionFeature extends HTMLElement {
    connectedCallback() {
      scheduleCollectionLayoutUpdate();
    }

    disconnectedCallback() {
      scheduleCollectionLayoutUpdate();
    }
  }

  if (!window.customElements.get('islander-collection-feature')) {
    window.customElements.define('islander-collection-feature', IslanderCollectionFeature);
  }

  class IslanderCollectionProductCarousel extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      this.viewport = this.querySelector('[data-carousel-viewport]');
      this.track = this.viewport?.querySelector('.islander-collection-product-carousel__track');
      this.originalPairs = Array.from(this.querySelectorAll('[data-carousel-pair]'));
      this.previousButton = this.querySelector('[data-carousel-previous]');
      this.nextButton = this.querySelector('[data-carousel-next]');
      this.status = this.querySelector('[data-carousel-status]');
      this.currentIndex = 0;
      this.currentPhysicalIndex = 0;
      this.autoScrollPaused = false;
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoScrollEnabled = this.dataset.autoScroll !== 'false';
      this.autoScrollDelay = Math.max(
        3000,
        Number(this.dataset.autoScrollDelay) || COLLECTION_CAROUSEL_AUTO_SCROLL_INTERVAL,
      );

      if (!this.viewport || !this.track || this.originalPairs.length === 0) return;

      this.setupLoop();
      const { signal } = this.abortController;

      this.previousButton?.addEventListener('click', () => {
        this.advance(-1);
        this.pauseAutoScrollTemporarily();
      }, { signal });
      this.nextButton?.addEventListener('click', () => {
        this.advance(1);
        this.pauseAutoScrollTemporarily();
      }, { signal });
      this.viewport.addEventListener('scroll', () => this.scheduleScrollUpdate(), { passive: true, signal });
      this.viewport.addEventListener('scrollend', () => this.normalizePosition(), { signal });
      this.viewport.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') this.setAutoScrollPaused(true);
      }, { signal });
      this.viewport.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'mouse') this.setAutoScrollPaused(false);
      }, { signal });
      this.viewport.addEventListener('focusin', () => this.setAutoScrollPaused(true), { signal });
      this.viewport.addEventListener('focusout', (event) => {
        if (!this.viewport.contains(event.relatedTarget)) this.setAutoScrollPaused(false);
      }, { signal });
      this.viewport.addEventListener('touchstart', () => this.pauseAutoScrollTemporarily(), {
        passive: true,
        signal,
      });
      document.addEventListener('visibilitychange', () => this.updateAutoScroll(), { signal });

      this.handleMotionChange = () => this.updateAutoScroll();
      this.motionQuery.addEventListener?.('change', this.handleMotionChange);

      this.handleResize = () => {
        const physicalIndex = this.loopBandStart + this.currentIndex;
        this.setPhysicalPosition(physicalIndex, 'auto');
        this.updateCarousel(this.currentIndex, physicalIndex);
      };
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.handleResize);
        this.resizeObserver.observe(this.viewport);
      } else {
        window.addEventListener('resize', this.handleResize, { signal });
      }

      window.requestAnimationFrame(() => {
        this.setPhysicalPosition(this.loopBandStart, 'auto');
        this.updateCarousel(0, this.loopBandStart);
        this.updateAutoScroll();
      });
    }

    disconnectedCallback() {
      this.stopAutoScroll();
      window.clearTimeout(this.autoScrollResumeTimer);
      window.clearTimeout(this.normalizeTimer);
      window.cancelAnimationFrame(this.scrollFrame);
      this.resizeObserver?.disconnect();
      this.motionQuery?.removeEventListener?.('change', this.handleMotionChange);
      this.abortController?.abort();
      this.abortController = null;
    }

    get visiblePairs() {
      return window.matchMedia('(min-width: 750px)').matches ? 2 : 1;
    }

    get productCount() {
      return Math.max(0, Number.parseInt(this.dataset.productCount || '0', 10) || 0);
    }

    setupLoop() {
      this.track.querySelectorAll('[data-carousel-clone]').forEach((clone) => clone.remove());
      this.loopBandStart = this.originalPairs.length > 1 ? this.originalPairs.length : 0;

      if (this.originalPairs.length > 1) {
        const beforeClones = document.createDocumentFragment();
        const afterClones = document.createDocumentFragment();
        this.originalPairs.forEach((pair) => {
          beforeClones.append(this.createClone(pair));
          afterClones.append(this.createClone(pair));
        });
        this.track.prepend(beforeClones);
        this.track.append(afterClones);
        this.classList.add('is-looping');
      }

      this.renderedPairs = Array.from(this.track.children).filter((element) =>
        element.matches('[data-carousel-pair], [data-carousel-clone]'),
      );
    }

    createClone(pair) {
      const clone = pair.cloneNode(true);
      clone.removeAttribute('data-carousel-pair');
      clone.setAttribute('data-carousel-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.inert = true;

      [clone, ...clone.querySelectorAll('*')].forEach((element) => {
        element.removeAttribute('id');
        element.removeAttribute('for');
        element.removeAttribute('aria-controls');
        element.removeAttribute('aria-describedby');
        element.removeAttribute('aria-labelledby');
        Array.from(element.attributes).forEach((attribute) => {
          if (attribute.name.startsWith('data-shopify')) element.removeAttribute(attribute.name);
        });
      });
      return clone;
    }

    getPairOffset(physicalIndex) {
      const pair = this.renderedPairs[physicalIndex];
      const firstPairOffset = this.renderedPairs[0]?.offsetLeft || 0;
      return pair ? pair.offsetLeft - firstPairOffset : 0;
    }

    setPhysicalPosition(physicalIndex, behavior = 'smooth') {
      if (!this.renderedPairs[physicalIndex]) return;
      this.currentPhysicalIndex = physicalIndex;
      this.viewport.scrollTo({
        left: this.getPairOffset(physicalIndex),
        behavior: this.motionQuery.matches ? 'auto' : behavior,
      });
    }

    advance(direction) {
      if (this.originalPairs.length < 2) return;
      const targetPhysicalIndex = this.currentPhysicalIndex + direction;
      const nextIndex = getCollectionCarouselLoopIndex(
        this.originalPairs.length,
        this.currentIndex + direction,
      );
      this.setPhysicalPosition(targetPhysicalIndex);
      this.updateCarousel(nextIndex, targetPhysicalIndex);
      this.scheduleNormalize();
    }

    scheduleNormalize() {
      window.clearTimeout(this.normalizeTimer);
      this.normalizeTimer = window.setTimeout(() => this.normalizePosition(), 480);
    }

    normalizePosition() {
      if (this.originalPairs.length < 2) return;
      const bandLength = this.originalPairs.length;
      let normalizedPhysicalIndex = this.currentPhysicalIndex;
      while (normalizedPhysicalIndex < this.loopBandStart) normalizedPhysicalIndex += bandLength;
      while (normalizedPhysicalIndex >= this.loopBandStart + bandLength) normalizedPhysicalIndex -= bandLength;
      if (normalizedPhysicalIndex === this.currentPhysicalIndex) return;
      this.setPhysicalPosition(normalizedPhysicalIndex, 'auto');
      this.updateCarousel(this.currentIndex, normalizedPhysicalIndex);
    }

    scheduleScrollUpdate() {
      if (this.scrollFrame) return;
      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = undefined;
        const nearestPhysicalIndex = this.renderedPairs.reduce((closestIndex, pair, index) => {
          const closestDistance = Math.abs(this.getPairOffset(closestIndex) - this.viewport.scrollLeft);
          const distance = Math.abs(this.getPairOffset(index) - this.viewport.scrollLeft);
          return distance < closestDistance ? index : closestIndex;
        }, 0);
        const nextIndex = getCollectionCarouselLoopIndex(
          this.originalPairs.length,
          nearestPhysicalIndex - this.loopBandStart,
        );
        this.currentPhysicalIndex = nearestPhysicalIndex;
        this.updateCarousel(nextIndex, nearestPhysicalIndex);
        this.scheduleNormalize();
      });
    }

    updateCarousel(requestedIndex, physicalIndex = this.currentPhysicalIndex) {
      this.currentIndex = getCollectionCarouselLoopIndex(this.originalPairs.length, requestedIndex);
      this.currentPhysicalIndex = physicalIndex;

      const controls = this.previousButton?.closest('.islander-collection-product-carousel__controls');
      if (controls) controls.hidden = this.originalPairs.length < 2;
      if (this.previousButton) this.previousButton.disabled = false;
      if (this.nextButton) this.nextButton.disabled = false;

      this.renderedPairs.forEach((pair, index) => {
        const isVisible = index >= physicalIndex && index < physicalIndex + this.visiblePairs;
        pair.toggleAttribute('aria-hidden', !isVisible);
        pair.inert = !isVisible;
      });

      if (this.status) {
        this.status.textContent = `Product group ${this.currentIndex + 1} of ${this.originalPairs.length}; ${this.productCount} products available`;
      }
    }

    setAutoScrollPaused(paused) {
      this.autoScrollPaused = paused;
      this.updateAutoScroll();
    }

    pauseAutoScrollTemporarily() {
      this.setAutoScrollPaused(true);
      window.clearTimeout(this.autoScrollResumeTimer);
      this.autoScrollResumeTimer = window.setTimeout(
        () => this.setAutoScrollPaused(false),
        COLLECTION_CAROUSEL_RESUME_DELAY,
      );
    }

    stopAutoScroll() {
      if (!this.autoScrollTimer) return;
      window.clearInterval(this.autoScrollTimer);
      this.autoScrollTimer = undefined;
    }

    updateAutoScroll() {
      this.stopAutoScroll();
      if (
        !this.autoScrollEnabled ||
        this.autoScrollPaused ||
        this.motionQuery.matches ||
        document.hidden ||
        this.originalPairs.length < 2
      ) return;

      this.autoScrollTimer = window.setInterval(() => this.advance(1), this.autoScrollDelay);
    }
  }

  if (!window.customElements.get('islander-collection-product-carousel')) {
    window.customElements.define(
      'islander-collection-product-carousel',
      IslanderCollectionProductCarousel,
    );
  }
}

if (
  typeof window !== 'undefined' &&
  typeof document !== 'undefined' &&
  'customElements' in window
) {
  registerCollectionLandingElements();
  THEME_EDITOR_EVENTS.forEach((eventName) => {
    document.addEventListener(eventName, scheduleCollectionLayoutUpdate);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleCollectionLayoutUpdate, { once: true });
  } else {
    scheduleCollectionLayoutUpdate();
  }
}
